import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import { WorkflowDetailsCard, SummarizationCard, QualityChecksCard, DocumentMatchingCard } from '@/components';
import type { ContentNode, QualityCheckNode, DocumentMetadata } from '@/types/workflow';
import { metadataApi } from '@/services/api';

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchWorkflow, updateWorkflow, deleteWorkflow } from '@/store/slices/workflowsSlice';

export default function WorkflowUpdateView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  const [title, setTitle] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [titleError, setTitleError] = useState('');
  const [tagsError, setTagsError] = useState('');
  const [contentNodes, setContentNodes] = useState<ContentNode[]>([]);
  const [qualityCheckNodes, setQualityCheckNodes] = useState<QualityCheckNode[]>([]);
  const [documentMatches, setDocumentMatches] = useState<DocumentMetadata[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const { items: availableTags } = useAppSelector((state) => state.tags) as {
    items: Array<{ id: number; name: string }>;
  };
  const { loading: workflowLoading, error: workflowError } = useAppSelector((state) => state.workflows) as {
    loading: boolean;
    error: string | null;
  };

  // Load metadata options and workflow data on component mount
  useEffect(() => {
    const loadData = async () => {
      if (!id) {
        navigate('/workflows/new');
        return;
      }

      try {
        setIsLoading(true);
        
        // Load metadata options and workflow in parallel
        const [proFormaData, documentTypeData, territoryData, workflow] = await Promise.all([
          metadataApi.getProFormaOptions(),
          metadataApi.getDocumentTypeOptions(),
          metadataApi.getTerritoryOptions(),
          dispatch(fetchWorkflow(parseInt(id))).unwrap()
        ]);
        
        // Create maps for label lookup
        const proFormaMap = new Map(proFormaData.map(opt => [opt.id, opt.name]));
        const documentTypeMap = new Map(documentTypeData.map(opt => [opt.id, opt.name]));
        const territoryMap = new Map(territoryData.map(opt => [opt.id, opt.name]));
        
        // Populate form with workflow data
        setTitle(workflow.title);
        setSelectedTags(workflow.tags.map(tag => tag.name));
        
        // Convert workflow nodes to content and quality check nodes
        const contentNodesData: ContentNode[] = [];
        const qualityCheckNodesData: QualityCheckNode[] = [];
        
        workflow.workflow_nodes.forEach(node => {
          if (node.node_type === 'CONTENT') {
            contentNodesData.push({
              id: node.id?.toString() || '',
              prompt: node.prompt || ''
            });
          } else if (node.node_type === 'QC') {
            qualityCheckNodesData.push({
              id: node.id?.toString() || '',
              prompt: node.prompt || '',
              passFailPrompt: node.pass_fail_prompt || ''
            });
          }
        });

        // Sort content nodes by sequence_order
        contentNodesData.sort((a, b) => {
          const nodeA = workflow.workflow_nodes.find(n => n.id?.toString() === a.id);
          const nodeB = workflow.workflow_nodes.find(n => n.id?.toString() === b.id);
          const orderA = nodeA?.sequence_order || 0;
          const orderB = nodeB?.sequence_order || 0;
          return orderA - orderB;
        });
        
        setContentNodes(contentNodesData);
        setQualityCheckNodes(qualityCheckNodesData);
        
        // Load document metadata if available
        if (workflow.metadata && workflow.metadata.length > 0) {
          const documentMatchesData = workflow.metadata.map((meta, index) => ({
            id: `metadata-${index}`,
            documentType: meta.proForma || '',
            documentTypeLabel: proFormaMap.get(meta.proForma) || meta.proForma || '',
            category: meta.documentType || '',
            categoryLabel: documentTypeMap.get(meta.documentType) || meta.documentType || '',
            status: meta.territory || '',
            statusLabel: territoryMap.get(meta.territory) || meta.territory || ''
          }));
          setDocumentMatches(documentMatchesData);
        }
      } catch (error) {
        console.error('Failed to load data:', error);
        alert('Failed to load workflow. Redirecting to create page.');
        navigate('/workflows/new');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [id, dispatch, navigate]);

  const handleUpdateWorkflow = async () => {
    // Reset previous errors
    setTitleError('');
    setTagsError('');

    // Validate title
    if (!title.trim()) {
      setTitleError('Workflow Title is required');
    }

    // Validate tags
    if (selectedTags.length === 0) {
      setTagsError('At least one tag is required');
    }

    // Validate content nodes
    const validContentNodes = contentNodes.filter(node => node.prompt && node.prompt.trim());
    if (validContentNodes.length === 0) {
      return;
    }

    // If there are validation errors, don't proceed
    if (!title.trim() || selectedTags.length === 0) {
      return;
    }

    try {
      // Convert selected tag names to tag IDs
      const tagIds = selectedTags
        .map(tagName => {
          const tag = availableTags.find(t => t.name === tagName);
          return tag?.id;
        })
        .filter((id): id is number => id !== undefined);

      // Convert content nodes to workflow nodes
      const contentWorkflowNodes = validContentNodes.map((node, index) => ({
        id: node.id ? parseInt(node.id) : undefined,
        node_type: 'CONTENT' as const,
        prompt: node.prompt.trim(),
        sequence_order: index + 1
      }));

      // Convert quality check nodes to workflow nodes
      const validQualityCheckNodes = qualityCheckNodes.filter(node => node.prompt && node.prompt.trim());
      const qualityCheckWorkflowNodes = validQualityCheckNodes.map((node) => ({
        id: node.id ? parseInt(node.id) : undefined,
        node_type: 'QC' as const,
        prompt: node.prompt.trim(),
        pass_fail_prompt: node.passFailPrompt.trim() || undefined,
        sequence_order: undefined // QC nodes don't have sequence order as they run in parallel
      }));

      // Combine all workflow nodes
      const workflowNodes = [...contentWorkflowNodes, ...qualityCheckWorkflowNodes];

      // Convert document metadata to API format
      const documentMetadata = documentMatches.map(match => ({
        proForma: match.documentType,
        documentType: match.category,
        territory: match.status
      }));

      // Create workflow update data
      const workflowData = {
        title: title.trim(),
        tag_ids: tagIds,
        workflow_nodes: workflowNodes,
        document_metadata: documentMetadata
      };

      // Dispatch the update workflow action
      const result = await dispatch(updateWorkflow({ id: parseInt(id!), workflowData })).unwrap();
      
      // Show success message and navigate back to workflows list
      console.log('Workflow updated successfully:', result);
      alert('Workflow updated successfully!');
      
      // Navigate to workflows list
      navigate('/workflows/');
      
    } catch (error) {
      console.error('Failed to update workflow:', error);
      setTitleError('Failed to update workflow. Please try again.');
    }
  };

  const handleDeleteWorkflow = async () => {
    if (!id) return;

    const confirmed = window.confirm('Are you sure you want to delete this workflow? This action cannot be undone.');
    if (!confirmed) return;

    try {
      await dispatch(deleteWorkflow(parseInt(id))).unwrap();
      alert('Workflow deleted successfully!');
      navigate('/workflows/new');
    } catch (error) {
      console.error('Failed to delete workflow:', error);
      alert('Failed to delete workflow. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ 
        flexGrow: 1, 
        backgroundColor: 'background.default',
        minHeight: '100vh',
        padding: 3,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div>Loading workflow...</div>
      </Box>
    );
  }

  if (workflowError) {
    return (
      <Box sx={{ 
        flexGrow: 1, 
        backgroundColor: 'background.default',
        minHeight: '100vh',
        padding: 3,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div>Error loading workflow: {workflowError}</div>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      flexGrow: 1, 
      backgroundColor: 'background.default',
      minHeight: '100vh',
      padding: 3
    }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/workflows')}
          sx={{ 
            textDecoration: 'none', 
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
        >
          Workflows
        </Link>
        <Typography color="text.primary">Edit Workflow</Typography>
      </Breadcrumbs>

      {/* Header with Cancel, Update and Delete Workflow Buttons */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        gap: 2,
        marginBottom: 3 
      }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/workflows')}
          disabled={workflowLoading}
          sx={{
            minWidth: 120
          }}
        >
          Cancel
        </Button>
        <Button
          variant="outlined"
          color="error"
          onClick={handleDeleteWorkflow}
          disabled={workflowLoading}
          sx={{
            minWidth: 120
          }}
        >
          {workflowLoading ? 'Deleting...' : 'Delete Workflow'}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleUpdateWorkflow}
          disabled={workflowLoading}
          sx={{
            minWidth: 120
          }}
        >
          {workflowLoading ? 'Updating...' : 'Update Workflow'}
        </Button>
      </Box>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        <Grid size={4}>
          <WorkflowDetailsCard 
            title={title}
            setTitle={setTitle}
            selectedTags={selectedTags}
            setSelectedTags={setSelectedTags}
            titleError={titleError}
            tagsError={tagsError}
          />
        </Grid>
        <Grid size={8}>
          <Stack spacing={2}>
            <SummarizationCard 
              contentNodes={contentNodes}
              setContentNodes={setContentNodes}
            />
            <QualityChecksCard 
              qualityCheckNodes={qualityCheckNodes}
              setQualityCheckNodes={setQualityCheckNodes}
            />
            <DocumentMatchingCard 
              documentMatches={documentMatches}
              onDocumentMatchesChange={setDocumentMatches}
            />
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
